/**
 * When the Inspector sweeps an organisation's library on its own: the
 * schedule an admin sets on the Inspector page, stored on the organisation
 * (`Organization.metadata`), read by the workspace server's scheduler and
 * shown by the page as "next run". One module so the two agree.
 *
 * Times are UTC hours on purpose: the scheduler ticks once an hour in UTC
 * and a firm's compliance team spans time zones rarely enough that a
 * stated UTC hour (shown alongside the viewer's local time) is clearer than
 * a zone that follows whoever last saved.
 */

export type InspectionCadence = "off" | "daily" | "weekly";

export interface InspectionSchedule {
  readonly cadence: InspectionCadence;
  /** 0–23, UTC. */
  readonly hourUtc: number;
  /** 0 (Sunday) – 6 (Saturday); only weekly runs use it. */
  readonly weekday: number;
}

/** Weekly, Monday 07:00 UTC — before the compliance team's week starts. */
export const DEFAULT_INSPECTION_SCHEDULE: InspectionSchedule = {
  cadence: "weekly",
  hourUtc: 7,
  weekday: 1,
};

export const INSPECTION_CADENCES: ReadonlyArray<InspectionCadence> = ["off", "daily", "weekly"];

const METADATA_CADENCE = "inspectorCadence";
const METADATA_HOUR = "inspectorHourUtc";
const METADATA_WEEKDAY = "inspectorWeekday";

const isCadence = (value: unknown): value is InspectionCadence =>
  value === "off" || value === "daily" || value === "weekly";

const intIn = (value: unknown, min: number, max: number): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= min && value <= max
    ? value
    : null;

/**
 * A legacy `daily` opt-in (set before this setting existed) ran at 06:30 UTC
 * and carries no explicit hour; default those to 06:00 so a deploy never
 * moves them *later*. Weekly's default stays 07:00, unchanged from before.
 */
export const LEGACY_DAILY_HOUR_UTC = 6;

/**
 * The schedule stored on an organisation's metadata; anything missing or
 * out of range falls back to the default, so an organisation that predates
 * the setting keeps its Monday sweep (and a legacy daily opt-in its morning).
 */
export function readInspectionSchedule(metadata: unknown): InspectionSchedule {
  if (typeof metadata !== "object" || metadata === null || Array.isArray(metadata)) {
    return DEFAULT_INSPECTION_SCHEDULE;
  }
  const record = metadata as Record<string, unknown>;
  const cadenceValue = record[METADATA_CADENCE];
  const cadence = isCadence(cadenceValue) ? cadenceValue : DEFAULT_INSPECTION_SCHEDULE.cadence;
  const defaultHour = cadence === "daily" ? LEGACY_DAILY_HOUR_UTC : DEFAULT_INSPECTION_SCHEDULE.hourUtc;
  return {
    cadence,
    hourUtc: intIn(record[METADATA_HOUR], 0, 23) ?? defaultHour,
    weekday: intIn(record[METADATA_WEEKDAY], 0, 6) ?? DEFAULT_INSPECTION_SCHEDULE.weekday,
  };
}

/** The metadata keys a schedule writes — merged over the rest of the blob. */
export function inspectionScheduleMetadata(
  schedule: InspectionSchedule,
): Record<string, string | number> {
  return {
    [METADATA_CADENCE]: schedule.cadence,
    [METADATA_HOUR]: schedule.hourUtc,
    [METADATA_WEEKDAY]: schedule.weekday,
  };
}

/** A schedule from untrusted input, or the reason it is not one. */
export function parseInspectionSchedule(
  input: unknown,
): { readonly ok: true; readonly value: InspectionSchedule } | { readonly ok: false; readonly reason: string } {
  if (typeof input !== "object" || input === null) {
    return { ok: false, reason: "Inspection schedule must be an object." };
  }
  const record = input as Record<string, unknown>;
  if (!isCadence(record.cadence)) {
    return { ok: false, reason: "Inspection cadence must be off, daily or weekly." };
  }
  const hourUtc = intIn(record.hourUtc, 0, 23);
  if (hourUtc === null) return { ok: false, reason: "Inspection hour must be 0–23 (UTC)." };
  const weekday = intIn(record.weekday, 0, 6);
  if (weekday === null) return { ok: false, reason: "Inspection weekday must be 0–6." };
  return { ok: true, value: { cadence: record.cadence, hourUtc, weekday } };
}

/**
 * Whether a scheduler tick at `at` should start this organisation's sweep:
 * the tick's UTC hour is the schedule's, and for a weekly schedule the
 * weekday too. The claim that keeps one run per day is the caller's.
 */
export function inspectionDue(schedule: InspectionSchedule, at: Date): boolean {
  if (schedule.cadence === "off") return false;
  if (at.getUTCHours() !== schedule.hourUtc) return false;
  return schedule.cadence === "daily" || at.getUTCDay() === schedule.weekday;
}

/** The next time the schedule fires after `now`; null when it is off. */
export function nextInspectionRun(schedule: InspectionSchedule, now = Date.now()): Date | null {
  if (schedule.cadence === "off") return null;
  const at = new Date(now);
  const next = new Date(
    Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate(), schedule.hourUtc, 0, 0, 0),
  );
  if (schedule.cadence === "weekly") {
    next.setUTCDate(next.getUTCDate() + ((schedule.weekday - next.getUTCDay() + 7) % 7));
  }
  if (next.getTime() <= now) {
    next.setUTCDate(next.getUTCDate() + (schedule.cadence === "weekly" ? 7 : 1));
  }
  return next;
}

export const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/** "Weekly on Monday at 07:00 UTC", "Daily at 07:00 UTC", "Off". */
export function describeInspectionSchedule(schedule: InspectionSchedule): string {
  if (schedule.cadence === "off") return "Off";
  const hour = `${String(schedule.hourUtc).padStart(2, "0")}:00 UTC`;
  return schedule.cadence === "daily"
    ? `Daily at ${hour}`
    : `Weekly on ${WEEKDAY_NAMES[schedule.weekday]} at ${hour}`;
}
